import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import noop from 'lodash/noop';
import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { useDispatch, useSelector } from 'react-redux';

import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TuneIcon from '@mui/icons-material/Tune';

import { ReactComponent as VisibilityIcon } from '../../../assets/images/icon-visibility.svg';
import { NO_FULFILLMENT_CENTER, NO_FULFILLMENT_CENTER_ITEM } from '../../../constants/fulfillmentCenters';
import { COLUMN_IDS, VISIBLE_REPORTS_COLS_INITIAL_STATE } from '../../../constants/reports';
import { INITIAL_PAGE } from '../../../constants/table';
import useClearFiltersCallback from '../../../hooks/useClearFiltersCallback';
import useColumnsVisibility from '../../../hooks/useColumnsVisibility';
import useCopyClipboard from '../../../hooks/useCopyClipboard';
import usePopover from '../../../hooks/usePopover';
import { changeReportsColumnsVisibility } from '../../../store/actions/tools';
import { selectFulfillmentCenterItems } from '../../../store/selectors/fulfillmentCenters';
import { selectIsProductMarketplaceEnabled } from '../../../store/selectors/organizations';
import { selectReportsVisibleColumns } from '../../../store/selectors/tools';
import { selectUsers } from '../../../store/selectors/users';
import { IReport, IReportsListFilters } from '../../../types/reports';
import { IFiltersRef } from '../../../types/tables';
import { ITimezone } from '../../../types/timezone';
import { detectUserTimezone, formatZonedDateTime, isValidDate, resolveStoredTimezone } from '../../../utils/date';
import { getFullName } from '../../../utils/helpers';
import { getHighlightStyles } from '../../../utils/highlightStyles';
import {
  calculateDaysLeft,
  getClaimUrl,
  getReportGiftBudget,
  getReportGiftBudgetUsage,
  getReportItemSources,
  getReportStatusAndDate,
  getReportStatusTooltipInfo,
  isCampaignPicked,
} from '../../../utils/reports';
import { defaultTableStyles, findFullNameByEmail, getDefaultStickyStyles } from '../../../utils/tables';
import { IPaginatedListChangeEvent } from '../../PaginatedList/PaginatedList';
import ReportsStatus from '../../ReportsStatus/ReportsStatus';
import ColumnsVisibilityPopover from '../../UsersComponent/components/ColumnsVisibilityPopover';
import {
  CopyableButton,
  CopyableLink,
  PageContent,
  TableFilteredEmptyPlaceholder,
  TableMUI,
  TableTooltip,
} from '../../_mui';
import ReportCampaignName from '../components/ReportCampaignName';
import ReportGiftBudget from '../components/ReportGiftBudget';
import ReportItemSource from '../components/ReportItemSource';
import { ReportsContextMenu } from '../components/ReportsContextMenu';
import ReportSendType from '../components/ReportSendType';
import { useReportsContextMenu } from '../hooks/useReportsContextMenu';

const WAREHOUSE_COL_WIDTH = 200;
const ITEM_SOURCE_COL_WIDTH = 240;
const GIFT_BUDGET_COL_WIDTH = 180;

const MARKETPLACE_COLUMN_IDS: string[] = [COLUMN_IDS.ITEM_SOURCE, COLUMN_IDS.GIFT_BUDGET];

interface IDateInfo {
  zonedDate: string | undefined;
  isValid: boolean;
  daysLeft?: string;
  timezone: {
    abbr: string;
    utc: string[];
  };
}

interface IProps {
  reports: IReport[];
  totalCount: number;
  onChange: ({ page, setPage }: IPaginatedListChangeEvent) => void;
  isLoading: boolean;
  filters: IReportsListFilters;
  page: number;
  pageSize: number;
}

const ReportsListView = forwardRef<IFiltersRef, IProps>(
  ({ reports, totalCount, onChange, isLoading, filters, page, pageSize }: IProps, filtersRef) => {
    const theme = useTheme();
    const userTimezone = detectUserTimezone();
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);

    const initialVisibleColumns = useSelector(selectReportsVisibleColumns);
    const users = useSelector(selectUsers);
    const fulfillmentCenters = useSelector(selectFulfillmentCenterItems);
    const isMarketplaceEnabled = useSelector(selectIsProductMarketplaceEnabled);
    const dispatch = useDispatch();

    const fulfillmentCenterNameById = useMemo(() => {
      const map = new Map<string, string>();
      (fulfillmentCenters ?? []).forEach((fc) => map.set(fc.uid, fc.name));
      return map;
    }, [fulfillmentCenters]);

    const { setCopied } = useCopyClipboard({ successDuration: 1000, toastDuration: 1000 });

    const { query: searchQuery, tab: activeTab } = filters;

    const handlePageChange = useCallback(
      (newPage: number) => {
        if (onChange) {
          onChange({
            page: newPage,
            pageSize,
            setPage: noop,
            setPageSize: noop,
          });
        }
      },
      [onChange, pageSize],
    );

    const handlePageSizeChange = useCallback(
      (newPageSize: number) => {
        if (onChange) {
          onChange({
            page: INITIAL_PAGE, // Reset to page 1 when page size changes
            pageSize: newPageSize,
            setPage: noop,
            setPageSize: noop,
          });
        }
      },
      [onChange],
    );

    const {
      anchorEl: visibleColsAnchorEl,
      position: popoverPosition,
      isOpen: isVisibleColsPopoverOpen,
      handleOpen: handleVisibleColsMenuOpen,
      handleClose: handleVisibleColsMenuClose,
    } = usePopover();

    const {
      visibleColumns,
      columnSearchQuery,
      setColumnSearchQuery,
      handleColumnVisibilityChange,
      handleToggleAllColumns,
      handleResetColumns,
      areAllColumnsVisible,
    } = useColumnsVisibility({
      initialState: initialVisibleColumns,
      requiredColumnIds: [COLUMN_IDS.ORDER_ID, COLUMN_IDS.ACTIONS],
      onChange: (newState: Record<string, boolean>) => dispatch(changeReportsColumnsVisibility(newState)),
      defaultColumns: VISIBLE_REPORTS_COLS_INITIAL_STATE,
    });

    const {
      menuAnchorEl,
      handleMenuOpen,
      handleMenuClose,
      handleCancelReport,
      canDeleteReport,
      handleDeleteReport,
      selectedItem,
      isNonCancelingReportStatus,
      canCancelReport,
      handleRowClick,
      canCopyTrackingUrl,
      canCopyShippingUrl,
      isMoreActionsDisabled,
    } = useReportsContextMenu({
      activeTab,
      pageSize,
      page,
    });

    const defaultColumnEvents = useMemo(
      () => ({
        onBodyCellDoubleClick: handleRowClick,
        onCellContextMenu: (e: React.MouseEvent<HTMLElement, MouseEvent>, report: IReport, columnId?: string) => {
          if (columnId !== COLUMN_IDS.ACTIONS) {
            e.preventDefault();
            setMenuPosition({
              top: e.pageY,
              left: e.pageX,
            });
            handleMenuOpen(e, report);
          }
        },
      }),
      [handleMenuOpen, handleRowClick],
    );

    const handleLocalMenuClose = useCallback(() => {
      setMenuPosition(null);
      handleMenuClose();
    }, [handleMenuClose]);

    const getDateInfo = useCallback(
      (date: string | null | undefined, isExpiration = false, timezone: ITimezone = userTimezone): IDateInfo => {
        const isValidData = !!(date && isValidDate(new Date(date)));
        let zonedDate;
        let daysLeft;

        if (isValidData) {
          zonedDate = formatZonedDateTime(date, timezone);
        }

        if (isValidData && isExpiration) {
          daysLeft = calculateDaysLeft(date);
        }
        return {
          isValid: isValidData,
          ...(isExpiration && { daysLeft }),
          timezone,
          zonedDate,
        };
      },
      [userTimezone],
    );

    const renderActionControls = useCallback(
      (report: IReport) => {
        const moreActionsDisabled = isMoreActionsDisabled(report);

        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Box
              onClick={(e) => {
                e.stopPropagation();
                handleRowClick(report);
              }}
              data-action="list_view_actions_btn"
            >
              <Tooltip title="View order" arrow placement="top">
                <VisibilityIcon color="action" />
              </Tooltip>
            </Box>
            <Box
              onClick={moreActionsDisabled ? undefined : (e) => handleMenuOpen(e, report)}
              sx={{ cursor: moreActionsDisabled ? 'not-allowed' : 'pointer' }}
              data-action="list_view_actions_btn"
            >
              <Tooltip title={moreActionsDisabled ? 'No actions available' : 'More actions'} arrow placement="top">
                <MoreVertIcon color={moreActionsDisabled ? 'disabled' : 'action'} />
              </Tooltip>
            </Box>
          </Box>
        );
      },
      [handleRowClick, handleMenuOpen, isMoreActionsDisabled],
    );

    const allColumns = useMemo(() => {
      const { stickyLeftCellStyle, actionsCellStyle } = getDefaultStickyStyles(theme, 85);
      const { bodyCellStyle, minWidthBodyCellStyle, ellipsisContainer } = defaultTableStyles(theme);
      const { mark, accentLink } = getHighlightStyles(theme);

      return [
        {
          id: COLUMN_IDS.ORDER_ID,
          label: 'Order ID',
          ...defaultColumnEvents,
          headerCellStyle: {
            ...stickyLeftCellStyle,
            minWidth: 200,
            zIndex: { xs: '2 !important', sm: '11 !important' },
            paddingLeft: 2.5,
          },
          bodyCellStyle: {
            ...bodyCellStyle,
            ...stickyLeftCellStyle,
            minWidth: 200,
          },
          render: (report: IReport) => (
            <CopyableLink
              value={report.order_id}
              onTextClick={() => handleRowClick(report)}
              textTooltip="View order"
              copyTooltip="Copy number"
              toastLabel="Order number"
            >
              <Highlighter
                highlightStyle={mark}
                searchWords={[searchQuery]}
                autoEscape
                textToHighlight={report.order_id || ''}
              />
            </CopyableLink>
          ),
        },
        {
          id: COLUMN_IDS.STATUS,
          label: 'Status',
          ...defaultColumnEvents,
          bodyCellStyle: {
            ...bodyCellStyle,
            minWidth: '170px',
          },
          render: (report: IReport) => {
            const { label, color: backgroundColor } = getReportStatusAndDate(report);
            const tooltipText = getReportStatusTooltipInfo(report);

            return (
              <TableTooltip title={tooltipText}>
                <span style={{ display: 'inline-block', width: '100%' }}>
                  <ReportsStatus status={label} backgroundColor={backgroundColor} />
                </span>
              </TableTooltip>
            );
          },
        },
        {
          id: COLUMN_IDS.CAMPAIGN_NAME,
          label: 'Campaign name',
          ...defaultColumnEvents,
          bodyCellStyle: {
            ...bodyCellStyle,
            minWidth: '160px',
          },
          render: (report: IReport) => (
            <ReportCampaignName
              preCreatedEngagementId={report.pre_created_engagement_id}
              campaignName={report.campaign_name}
              searchQuery={searchQuery}
              isPYGChipShown={!isCampaignPicked(report)}
              highlightStyle={mark}
            />
          ),
        },
        {
          id: COLUMN_IDS.SEND_TYPE,
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          label: 'Send type',
          render: (report: IReport) => <ReportSendType sendType={report.engagement_source_type} />,
        },
        {
          id: COLUMN_IDS.ITEM_SOURCE,
          label: 'Item source',
          ...defaultColumnEvents,
          headerCellStyle: { minWidth: ITEM_SOURCE_COL_WIDTH, maxWidth: ITEM_SOURCE_COL_WIDTH },
          bodyCellStyle: { ...bodyCellStyle, minWidth: ITEM_SOURCE_COL_WIDTH, maxWidth: ITEM_SOURCE_COL_WIDTH },
          render: (report: IReport) => <ReportItemSource sources={getReportItemSources(report)} />,
        },
        {
          id: COLUMN_IDS.GIFT_BUDGET,
          label: 'Gift budget',
          ...defaultColumnEvents,
          headerCellStyle: { minWidth: GIFT_BUDGET_COL_WIDTH, maxWidth: GIFT_BUDGET_COL_WIDTH },
          bodyCellStyle: { ...bodyCellStyle, minWidth: GIFT_BUDGET_COL_WIDTH, maxWidth: GIFT_BUDGET_COL_WIDTH },
          render: (report: IReport) => (
            <ReportGiftBudget budget={getReportGiftBudget(report)} usage={getReportGiftBudgetUsage(report)} />
          ),
        },
        {
          id: COLUMN_IDS.CREATED_BY,
          label: 'Created by',
          ...defaultColumnEvents,
          bodyCellStyle,
          render: (report: IReport) => {
            const { created_by } = report;
            const fullName = findFullNameByEmail(users, created_by);
            return (
              <Box>
                <Tooltip title={fullName} arrow placement="top">
                  <Typography component="span" sx={{ ...ellipsisContainer }}>
                    {fullName ?? ''}
                  </Typography>
                </Tooltip>
                <Tooltip title={created_by} arrow placement="top">
                  <Typography component="span" sx={{ ...ellipsisContainer, color: (th) => th.palette.text.secondary }}>
                    {created_by}
                  </Typography>
                </Tooltip>
              </Box>
            );
          },
        },
        {
          id: COLUMN_IDS.SENDER,
          label: 'Sender',
          ...defaultColumnEvents,
          bodyCellStyle,
          render: (report: IReport) => {
            const fullName = getFullName(report.sender_first_name, report.sender_last_name);
            return (
              <Box>
                <Tooltip title={fullName} arrow placement="top">
                  <Typography component="span" sx={{ ...ellipsisContainer }}>
                    <Highlighter
                      highlightStyle={mark}
                      searchWords={[searchQuery]}
                      autoEscape
                      textToHighlight={fullName || ''}
                    />
                  </Typography>
                </Tooltip>
                <Tooltip title={report.sender_email} arrow placement="top">
                  <Typography component="span" color="text.secondary">
                    <Highlighter
                      highlightStyle={mark}
                      searchWords={[searchQuery]}
                      autoEscape
                      textToHighlight={report.sender_email || ''}
                    />
                  </Typography>
                </Tooltip>
              </Box>
            );
          },
        },
        {
          id: COLUMN_IDS.RECEIVER,
          label: 'Receiver',
          ...defaultColumnEvents,
          bodyCellStyle,
          render: (report: IReport) => {
            const fullName = getFullName(report.receiver_first_name, report.receiver_last_name);
            return (
              <Box>
                <Typography>
                  <Highlighter
                    highlightStyle={mark}
                    searchWords={[searchQuery]}
                    autoEscape
                    textToHighlight={fullName || ''}
                  />
                </Typography>
                <Typography color="text.secondary">
                  <Highlighter
                    highlightStyle={mark}
                    searchWords={[searchQuery]}
                    autoEscape
                    textToHighlight={report.receiver_email || ''}
                  />
                </Typography>
              </Box>
            );
          },
        },
        {
          id: COLUMN_IDS.WAREHOUSE,
          label: 'Warehouse',
          ...defaultColumnEvents,
          headerCellStyle: { minWidth: WAREHOUSE_COL_WIDTH, maxWidth: WAREHOUSE_COL_WIDTH },
          bodyCellStyle: { ...bodyCellStyle, minWidth: WAREHOUSE_COL_WIDTH, maxWidth: WAREHOUSE_COL_WIDTH },
          render: (report: IReport) => {
            const apiNames = (report.fulfillment_center_names ?? []).filter(
              (name) => name !== NO_FULFILLMENT_CENTER_ITEM.name,
            );
            const fallbackNames =
              apiNames.length === 0
                ? Array.from(
                    new Set(
                      (report.items ?? [])
                        .filter((item) => item.fulfillment_center_id !== NO_FULFILLMENT_CENTER)
                        .map((item) => fulfillmentCenterNameById.get(item.fulfillment_center_id))
                        .filter((name): name is string => !!name),
                    ),
                  )
                : [];
            const names = apiNames.length > 0 ? apiNames : fallbackNames;
            if (names.length === 0) {
              return <Typography>—</Typography>;
            }
            const text = names.join(', ');
            return (
              <TableTooltip title={text}>
                <Typography sx={{ ...ellipsisContainer, maxWidth: WAREHOUSE_COL_WIDTH }}>{text}</Typography>
              </TableTooltip>
            );
          },
        },
        {
          id: COLUMN_IDS.SHIPMENT_ID,
          label: 'Shipment ID',
          ...defaultColumnEvents,
          bodyCellStyle,
          render: (report: IReport) => {
            return (
              <Box sx={{ minWidth: '170px' }}>
                {report.ship_order_id && (
                  <CopyableButton
                    aria-label="Copy shipment ID"
                    value={String(report.ship_order_id)}
                    toastLabel="Shipment ID"
                    style={{
                      color: theme.sass?.ebony,
                      '&:hover': {
                        color: 'primary.main',
                      },
                    }}
                  >
                    <span>{report.ship_order_id}</span>
                  </CopyableButton>
                )}
              </Box>
            );
          },
        },
        {
          id: COLUMN_IDS.CLAIM_LINK,
          label: 'Claim URL',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (report: IReport) => {
            const claimUrl = getClaimUrl(report);
            return claimUrl ? (
              <CopyableButton
                value={claimUrl}
                aria-label="Copy claim URL"
                tooltipText="Copy URL"
                toastLabel="Claim URL"
              >
                <span style={accentLink}>Claim link</span>
              </CopyableButton>
            ) : null;
          },
        },
        {
          id: COLUMN_IDS.TRACKING_URL,
          label: 'Tracking URL',
          ...defaultColumnEvents,
          bodyCellStyle: { minWidth: '180px' },
          render: (report: IReport) => {
            return report.tracking_url ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  size="small"
                  sx={{ padding: 0.5 }}
                  color="primary"
                  aria-label="Copy tracking URL"
                  onClick={() => setCopied(String(report.tracking_url), 'Tracking URL')}
                >
                  <ContentCopyOutlinedIcon />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Open tracking URL in new tab"
                  onClick={() => window.open(String(report.tracking_url), '_blank', 'noopener,noreferrer')}
                  sx={{ padding: 0.5 }}
                  color="primary"
                >
                  <OpenInNewIcon />
                </IconButton>
              </Box>
            ) : null;
          },
        },
        {
          id: COLUMN_IDS.TRACKING_NUMBER,
          label: 'Tracking number',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (report: IReport) => {
            return <Typography>{report.tracking_number}</Typography>;
          },
        },
        {
          id: COLUMN_IDS.COURIER,
          label: 'Courier',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (report: IReport) => {
            return <Typography>{report.ship_carrier || ''}</Typography>;
          },
        },
        {
          id: COLUMN_IDS.CREATED_DATE,
          label: 'Date created',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (report: IReport) => {
            const { zonedDate, isValid } = getDateInfo(report.created_at);
            return isValid ? <Typography>{zonedDate}</Typography> : null;
          },
        },
        {
          id: COLUMN_IDS.EXPIRATION_DATE,
          label: 'Expiration date',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (report: IReport) => {
            const { zonedDate, isValid, daysLeft } = getDateInfo(
              report.delayed_shipping_expiration,
              true,
              resolveStoredTimezone(report.timezone_metadata, 'delayed_shipping_expiration'),
            );

            return (
              <>
                {isValid && (
                  <TableTooltip title={daysLeft || ''}>
                    <Typography sx={{ ...bodyCellStyle }}>{zonedDate}</Typography>
                  </TableTooltip>
                )}
              </>
            );
          },
        },
        {
          id: COLUMN_IDS.VIEW_DATE,
          label: 'View date',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (report: IReport) => {
            const { zonedDate, isValid } = getDateInfo(report.delayed_shipping_page_view_date);
            return isValid ? <Typography>{zonedDate}</Typography> : null;
          },
        },
        {
          id: COLUMN_IDS.SUBMITTED_DATE,
          label: 'Submitted date',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (report: IReport) => {
            const { zonedDate, isValid } = getDateInfo(report.delayed_shipping_submission_date);
            return isValid ? <Typography>{zonedDate}</Typography> : null;
          },
        },
        {
          id: COLUMN_IDS.SHIPPED_DATE,
          label: 'Shipped date',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (report: IReport) => {
            const { zonedDate, isValid } = getDateInfo(report.package_shipped_date);
            return isValid ? <Typography>{zonedDate}</Typography> : null;
          },
        },
        {
          id: COLUMN_IDS.DELIVERED_DATE,
          label: 'Delivered date',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (report: IReport) => {
            const { zonedDate, isValid } = getDateInfo(report.package_delivered_date);
            return isValid ? <Typography>{zonedDate}</Typography> : null;
          },
        },
        {
          id: COLUMN_IDS.ACTIONS,
          label: (
            <TableTooltip title="Show/hide columns">
              <TuneIcon color="action" />
            </TableTooltip>
          ),
          headerCellStyle: actionsCellStyle,
          bodyCellStyle: actionsCellStyle,
          onHeaderCellClick: (e: React.MouseEvent<HTMLElement>) => {
            handleVisibleColsMenuOpen(e);
          },
          render: renderActionControls,
        },
      ].filter((column) => isMarketplaceEnabled || !MARKETPLACE_COLUMN_IDS.includes(column.id));
    }, [
      defaultColumnEvents,
      handleVisibleColsMenuOpen,
      renderActionControls,
      searchQuery,
      setCopied,
      theme,
      users,
      getDateInfo,
      fulfillmentCenterNameById,
      handleRowClick,
      isMarketplaceEnabled,
    ]);

    const columns = useMemo(() => {
      return allColumns.filter((column) => visibleColumns[column.id]);
    }, [allColumns, visibleColumns]);

    const handleClearFilters = useClearFiltersCallback(
      filtersRef && typeof filtersRef !== 'function' ? filtersRef : null,
    );

    const placeholder = useMemo(
      () => <TableFilteredEmptyPlaceholder itemType="delivery reports" onClearFilters={handleClearFilters} />,
      [handleClearFilters],
    );

    return (
      <PageContent>
        <TableMUI
          maxHeight="unset"
          containerStyle={{ width: '100%' }}
          data={reports}
          columns={columns}
          page={page}
          rowsPerPage={pageSize}
          totalCount={totalCount}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePageSizeChange}
          isLoading={isLoading}
          emptyPlaceholderComponent={placeholder}
          activeRow={selectedItem}
          menuAnchorEl={menuAnchorEl}
          handleMenuClose={handleLocalMenuClose}
        />
        <ReportsContextMenu
          anchorEl={menuAnchorEl}
          menuPosition={menuPosition}
          selectedItem={selectedItem}
          isNonCancelingReportStatus={isNonCancelingReportStatus}
          canCancelReport={canCancelReport}
          canDeleteReport={canDeleteReport}
          canCopyShippingUrl={canCopyShippingUrl}
          canCopyTrackingUrl={canCopyTrackingUrl}
          onClose={handleLocalMenuClose}
          onCancelReport={handleCancelReport}
          onDeleteReport={handleDeleteReport}
        />
        <ColumnsVisibilityPopover
          anchorEl={visibleColsAnchorEl}
          position={popoverPosition}
          open={isVisibleColsPopoverOpen}
          onClose={handleVisibleColsMenuClose}
          columns={allColumns}
          visibleColumns={visibleColumns}
          searchQuery={columnSearchQuery}
          onSearchChange={setColumnSearchQuery}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          onToggleAll={handleToggleAllColumns}
          onReset={handleResetColumns}
          requiredColumnIds={[COLUMN_IDS.ORDER_ID, COLUMN_IDS.ACTIONS]}
          areAllColumnsVisible={areAllColumnsVisible}
        />
      </PageContent>
    );
  },
);

export default ReportsListView;
